import * as THREE from 'three'

/**
 * LotusFlower - A complete lotus flower with multiple animated layers
 * Creates multiple layers of petals, each layer rotated and offset for a full 3D effect
 */
export class LotusFlower {
  constructor(parent, lotusGltf, options = {}) {
    this.parent = parent
    this.lotusGltf = lotusGltf
    this.options = {
      petalCount: 6,
      layerCount: 6,              // Number of layers
      layerRotationStep: 40,      // Degrees between layers
      layerOffsetFrames: 25,      // Frame offset between layers
      petalRadius: 0.8,
      petalScale: 3.0,
      color: 0xffffff,
      map: null,                  // Optional external texture override
      lotusIndex: 0,
      ...options
    }
    
    this.layers = []              // Array of layer groups
    this.meshes = []              // All meshes for raycasting
    this.mixers = []              // All animation mixers
    this.actions = []             // All animation actions
    this.animations = lotusGltf.animations || []
    
    // Animation control properties
    this.isPlaying = true
    this.initialPlaybackComplete = false
    this.playTime = 0
    this.initialDuration = 60 / 60 // 60 frames at 60fps = 1 second
    this.isHovered = false
    
    this.build()
  }
  
  build() {
    const { petalCount, layerCount, petalScale, color, lotusIndex, map } = this.options
    
    // Create a group to hold all layers
    this.group = new THREE.Group()
    this.parent.add(this.group)
    
    // Get the original petal to calculate scale
    const originalPetal = this.lotusGltf.scene
    const box = new THREE.Box3().setFromObject(originalPetal)
    const size = box.getSize(new THREE.Vector3())
    const maxDim = Math.max(size.x, size.y, size.z)
    
    // Safety check for zero-size meshes
    let scale = petalScale
    if (maxDim > 0.001) {
      scale = petalScale / maxDim
    } else {
      console.warn(`LotusFlower ${lotusIndex}: mesh has near-zero size (${maxDim}), using default scale`)
    }
    
    // Create a processed template petal once, then clone it for each instance.
    
    this.petalTemplate = this.createPetalTemplate(originalPetal, scale, color, lotusIndex)
    
    // Create multiple layers
    for (let layerIndex = 0; layerIndex < layerCount; layerIndex++) {
      const layer = this.createLayer(layerIndex, scale, color, lotusIndex)
      this.group.add(layer)
      this.layers.push(layer)
    }
  }
  
  createPetalTemplate(sourceScene, scale, color, lotusIndex) {
    const template = sourceScene.clone()
    let meshCount = 0
    let texturedMeshCount = 0
    
    template.traverse((child) => {
      if (child.isMesh) {
        meshCount++
        const externalMap = this.options.map
        const embeddedMap = child.material?.map || null
        const hasEmbeddedMap = embeddedMap !== null
        const hasUv = child.geometry?.attributes?.uv !== undefined
        
        // Only apply external texture if the mesh already had a texture (petal mesh).
        // Also require UVs — otherwise the texture can't map.
        const useExternalMap = externalMap && hasEmbeddedMap && hasUv
        const activeMap = useExternalMap ? externalMap : embeddedMap
        const hasTexture = activeMap !== null && hasUv
        
        if (useExternalMap) texturedMeshCount++
        
        if (child.material) {
          const baseColor = new THREE.Color(hasTexture ? 0xffffff : color)
          const fresnelColor = new THREE.Color(hasTexture ? 0xffffff : color).multiplyScalar(1.0)
          
          child.material = new THREE.ShaderMaterial({
            uniforms: {
              uColor: { value: baseColor },
              uFresnelColor: { value: fresnelColor },
              uFresnelPower: { value: 0.5 },
              uFresnelIntensity: { value: 0.6 },
              uTime: { value: 0.0 },
              uPulseSpeed: { value: 1.0 },
              uPulseIntensity: { value: 0.15 },
              uOpacity: { value: 1.0 },
              uMap: { value: activeMap },
              uHasTexture: { value: hasTexture ? 1.0 : 0.0 }
            },
            vertexShader: `
              varying vec3 vNormal;
              varying vec3 vViewDirection;
              varying vec3 vWorldPosition;
              varying vec2 vUv;
              
              void main() {
                vec4 worldPosition = modelMatrix * vec4(position, 1.0);
                vWorldPosition = worldPosition.xyz;
                vNormal = normalize(normalMatrix * normal);
                vViewDirection = normalize(cameraPosition - worldPosition.xyz);
                vUv = uv;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
              }
            `,
            fragmentShader: `
              uniform vec3 uColor;
              uniform vec3 uFresnelColor;
              uniform float uFresnelPower;
              uniform float uFresnelIntensity;
              uniform float uTime;
              uniform float uPulseSpeed;
              uniform float uPulseIntensity;
              uniform float uOpacity;
              uniform sampler2D uMap;
              uniform float uHasTexture;
              
              varying vec3 vNormal;
              varying vec3 vViewDirection;
              varying vec3 vWorldPosition;
              varying vec2 vUv;
              
              void main() {
                float fresnel = 1.0 - dot(vNormal, vViewDirection);
                fresnel = clamp(fresnel, 0.0, 1.0);
                fresnel = pow(fresnel, uFresnelPower);
                
                float pulse = sin(uTime * uPulseSpeed) * 0.5 + 0.5;
                float pulseFactor = 1.0 + pulse * uPulseIntensity;
                
                vec3 baseColor = uColor;
                if (uHasTexture > 0.5) {
                  vec4 texColor = texture2D(uMap, vUv);
                  baseColor = texColor.rgb;
                }
                
                vec3 finalColor = mix(baseColor, uFresnelColor * pulseFactor, fresnel * uFresnelIntensity);
                finalColor += uFresnelColor * fresnel * uFresnelIntensity * 0.1;
                
                // Clamp to prevent overexposure
                finalColor = clamp(finalColor, 0.0, 1.0);
                float alpha = mix(1.0, 0.45, fresnel) * uOpacity;
                
                gl_FragColor = vec4(finalColor, alpha);
              }
            `,
            transparent: true,
            side: THREE.DoubleSide,
            depthWrite: true
          })
          
          child.visible = true
        }
      }
    })
    

    
    // Apply scale to template so clones inherit it
    template.scale.setScalar(scale)
    template.userData.targetScale = scale
    
    return template
  }
  
  createLayer(layerIndex, scale, color, lotusIndex) {
    const { petalCount, petalRadius, layerRotationStep, layerOffsetFrames } = this.options
    
    // Create a group for this layer
    const layerGroup = new THREE.Group()
    
    // Create petals for this layer
    for (let petalIndex = 0; petalIndex < petalCount; petalIndex++) {
      const petal = this.createPetal(petalIndex, scale, petalRadius, color, lotusIndex, layerIndex, layerOffsetFrames)
      layerGroup.add(petal)
    }
    
    // Rotate entire layer
    const layerRotation = layerIndex * layerRotationStep * (Math.PI / 180)
    layerGroup.rotation.y = layerRotation
    
    return layerGroup
  }
  
  createPetal(petalIndex, scale, radius, color, lotusIndex, layerIndex, layerOffsetFrames) {
    // Clone the pre-processed template (materials & textures already applied)
    const petal = this.petalTemplate.clone()
    
    // Keep petals at the layer centre so they radiate from a single pivot
    const angle = (petalIndex / this.options.petalCount) * Math.PI * 2
    petal.position.set(0, 0, 0)
    petal.rotation.y = angle
    
    // Store metadata
    petal.userData = {
      lotusIndex: lotusIndex,
      layerIndex: layerIndex,
      petalIndex: petalIndex,
      targetScale: scale
    }
    
    // Register meshes for raycasting and update userdata
    petal.traverse((child) => {
      if (child.isMesh) {
        child.userData = { lotusIndex: lotusIndex, isLotus: true }
        this.meshes.push(child)
      }
    })
    
    // Setup animations with layer-based offset
    if (this.animations.length > 0) {
      const mixer = new THREE.AnimationMixer(petal)
      
      this.animations.forEach((clip) => {
        const action = mixer.clipAction(clip)
        action.setLoop(THREE.LoopRepeat)
        
        // Apply frame offset based on layer index
        const frameOffset = layerIndex * layerOffsetFrames
        const frameOffsetSeconds = frameOffset / 60 // Convert frames to seconds (assuming 60fps)
        
        // Set the action time to the offset so it starts from there
        action.time = frameOffsetSeconds
        
        action.play()
        this.actions.push(action)
      })
      
      this.mixers.push(mixer)
    }
    
    return petal
  }
  
  // Set world position of the entire lotus
  setPosition(x, y, z) {
    this.group.position.set(x, y, z)
  }
  
  // Set rotation of the entire lotus group
  setRotation(x, y, z) {
    this.group.rotation.set(x, y, z)
  }
  
  // Set local position (if parent is already positioned)
  setLocalPosition(x, y, z) {
    this.group.position.set(x, y, z)
  }
  
  // Entrance animation - scale up all layers
  animateEntrance(delay = 0, duration = 0.6) {
    this.layers.forEach((layer, layerIndex) => {
      layer.scale.set(0, 0, 0)
      
      setTimeout(() => {
        this.animateScale(layer, 1, duration)
      }, delay + layerIndex * 100) // Stagger layers
    })
  }
  
  animateScale(object, target, duration) {
    const start = 0
    const startTime = performance.now()
    
    const animate = () => {
      const elapsed = (performance.now() - startTime) / 1000
      const progress = Math.min(elapsed / duration, 1)
      const ease = 1 - Math.pow(1 - progress, 3)
      
      const current = start + (target - start) * ease
      object.scale.set(current, current, current)
      
      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }
    
    animate()
  }
  
  // Play animation
  playAnimation() {
    if (!this.isPlaying) {
      this.actions.forEach(action => action.paused = false)
      this.isPlaying = true
    }
  }
  
  // Pause animation
  pauseAnimation() {
    if (this.isPlaying) {
      this.actions.forEach(action => action.paused = true)
      this.isPlaying = false
    }
  }
  
  // Set hover state
  setHovered(hovered) {
    this.isHovered = hovered
    if (hovered) {
      this.playAnimation()
    } else if (this.initialPlaybackComplete) {
      this.pauseAnimation()
    }
  }
  
  // Update animations
  update(delta, time) {
    // Handle initial 60 frame playback
    if (!this.initialPlaybackComplete) {
      this.playTime += delta
      if (this.playTime >= this.initialDuration) {
        this.pauseAnimation()
        this.initialPlaybackComplete = true
      }
    }
    
    // Update animation mixers
    this.mixers.forEach((mixer) => {
      mixer.update(delta)
    })
    
    // Update fresnel shader uniforms
    if (time !== undefined) {
      this.meshes.forEach((mesh) => {
        if (mesh.material && mesh.material.uniforms && mesh.material.uniforms.uTime) {
          mesh.material.uniforms.uTime.value = time
        }
      })
    }
  }
  
  // Get all meshes for raycasting
  getMeshes() {
    return this.meshes
  }
  
  // Destroy and cleanup
  destroy() {
    this.mixers.forEach(mixer => mixer.stopAllAction())
    this.mixers = []
    
    this.meshes = []
    this.layers = []
    
    if (this.group && this.parent) {
      this.parent.remove(this.group)
    }
    this.group = null
  }
}
